import React, { useState } from 'react'
import PropTypes from 'prop-types'

import CoverageModal from './coverage-modal'
import { gapsIn, notSyncedIn, number, percent, when } from './format'
import './styles.scss'

// Why the audiences a row counts are not synced, in the bar's order after
// synced - each part's colour is its class. Together with synced they make up
// every audience counted.
const PARTS = [
  { key: 'not_synced', label: 'missing' },
  { key: 'inactive', label: 'inactive' },
  { key: 'unchecked', label: 'not checked yet' },
  { key: 'no_id', label: 'with no ID' },
]

// One distribution's coverage in the partner it goes to, as a read-only field
// among the distribution's own - labelled like them, with "See which" beside
// the label as "Add All Countries" sits beside its, and the partner and when
// its list was taken where the other fields put their hints. In the box: how
// many of its audiences the partner has - from its last nightly list, plus
// anything pushed since - what became of the rest, and a bar of the same.
const DistributionCoverage = ({ row, taxonomy }) => {
  const [open, setOpen] = useState(false)
  const { counts } = row
  const scanned = row.state === 'ok'

  // anything to look at - not synced, or left off the allowlist
  const gaps = gapsIn(row)
  // The figure's tone: amber while any are not synced, green once every one
  // is. An allowlist left behind is flagged beside it instead.
  const tone = !scanned ? '' : notSyncedIn(row) ? 'has-gaps' : 'is-covered'
  const parts = PARTS.filter((part) => counts[part.key])
  const share = (key) => `${(counts[key] / counts.audiences) * 100}%`
  const canOpen = gaps > 0

  return (
    <div className={`distribution-coverage ${tone}`}>
      <div className="distribution-coverage__label-row">
        <span className="distribution-coverage__label">
          Partner Coverage
          <If condition={canOpen}>
            <button
              type="button"
              className="rounded-md p-1 px-2 ml-3 w-fit font-semibold text-sm bg-primary text-gray-100 cursor-pointer"
              onClick={() => setOpen(true)}
            >
              See which
            </button>
          </If>
        </span>
        <span className="distribution-coverage__hint">
          {row.partner.name}
          {scanned ? ` · as of ${when(row.snapshot?.taken_at)}` : ''}
        </span>
      </div>

      <div className="distribution-coverage__box">
        <Choose>
          <When condition={!scanned}>
            <span className="is-quiet">Not scanned yet</span>
            <If condition={row.problem}>
              <span className="is-quiet"> - {row.problem}</span>
            </If>
          </When>
          <Otherwise>
            <div className="distribution-coverage__summary">
              <span className="distribution-coverage__figure">
                <span className="distribution-coverage__dot is-synced" />
                {number(counts.synced)} of {number(counts.audiences)} synced{' '}
                <span className="distribution-coverage__percent">
                  ({percent(counts.synced, counts.audiences)})
                </span>
              </span>
              <ul className="distribution-coverage__legend">
                <For each="part" of={parts}>
                  <li key={part.key}>
                    <span className={`distribution-coverage__dot is-${part.key}`} />
                    {number(counts[part.key])} {part.label}
                  </li>
                </For>
                {/* not a part of the bar: it overlaps the rest, synced or not */}
                <If condition={counts.not_allowed}>
                  <li className="is-flag">
                    <span className="distribution-coverage__dot is-flag" />
                    {number(counts.not_allowed)} not on the allowlist
                  </li>
                </If>
                <If condition={counts.blocked}>
                  <li className="is-quiet">{number(counts.blocked)} blocklisted</li>
                </If>
              </ul>
            </div>
            {/* the words above say the same */}
            <If condition={counts.audiences}>
              <div className="distribution-coverage__bar" aria-hidden="true">
                <If condition={counts.synced}>
                  <span
                    className="distribution-coverage__segment is-synced"
                    style={{ width: share('synced') }}
                  />
                </If>
                <For each="part" of={parts}>
                  <span
                    key={part.key}
                    className={`distribution-coverage__segment is-${part.key}`}
                    style={{ width: share(part.key) }}
                  />
                </For>
              </div>
            </If>
          </Otherwise>
        </Choose>
      </div>

      <If condition={canOpen}>
        <CoverageModal row={row} taxonomy={taxonomy} isOpen={open} onClose={() => setOpen(false)} />
      </If>
    </div>
  )
}

DistributionCoverage.propTypes = {
  row: PropTypes.shape({
    state: PropTypes.string,
    problem: PropTypes.string,
    filtered: PropTypes.bool,
    partner: PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string }),
    counts: PropTypes.shape({
      audiences: PropTypes.number,
      synced: PropTypes.number,
      inactive: PropTypes.number,
      not_synced: PropTypes.number,
      unchecked: PropTypes.number,
      not_allowed: PropTypes.number,
      no_id: PropTypes.number,
      blocked: PropTypes.number,
    }),
    snapshot: PropTypes.object,
  }).isRequired,
  taxonomy: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }).isRequired,
}

export default DistributionCoverage
